import { IUserOutputDto } from '@quadminds/shared/auth-system';
import { ApiStandardResponse } from '@quadminds/shared/commons';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../services/users.service';

/**
 * Custom hook to fetch paginated users of an organization using React Query.
 *
 * @param organizationId - The ID of the organization to fetch users for.
 * @param page - The current page number.
 * @param limit - The number of users per page.
 * @param searchQuery - The search query to filter users by firstName or lastName.
 * @param statusFilter - The filter to include users based on their active status.
 * @param roleFilter - The filter to include users based on their role.
 * @returns An object containing the query state and data.
 */
export const useOrganizationUsers = (
    organizationId: string,
    page: number,
    limit: number,
    searchQuery?: string,
    statusFilter?: string,
    roleFilter?: string,
    appId?: string,
) => {
    return useQuery<ApiStandardResponse<IUserOutputDto[]>, Error>({
        queryKey: ['organizationUsers', organizationId, page, limit, searchQuery, statusFilter, roleFilter, appId], // Query key
        queryFn: async () => {
            if (!organizationId) {
                throw new Error('Organization ID is required to fetch users');
            }

            // Construimos el filtro dinámicamente
            const filter: Record<string, any> = {};

            if (searchQuery) {
                filter['$or'] = [
                    { firstName: { $regex: searchQuery, $options: 'i' } },
                    { lastName: { $regex: searchQuery, $options: 'i' } },
                ];
            }

            if (statusFilter && appId && statusFilter !== 'all') {
                filter['userOrganizations.applications'] =
                    statusFilter === 'active' ? { $oid: appId } : { $ne: { $oid: appId } };
            }

            if (roleFilter && roleFilter !== 'all') {
                filter['userOrganizations.roles'] = { $oid: roleFilter };
            }

            const response = await usersApi.getOrganizationUsers(organizationId, page, limit, JSON.stringify(filter));
            return response;
        },
        enabled: !!organizationId, // Only run the query if organizationId is provided
    });
};
